/**
 * Pure entry/eligibility logic. No DOM access — unit-tested in entries.test.js.
 *
 * CRITICAL INVARIANT (no duplicate winners): once a username appears in the
 * winners list — in ANY tier, voided or not — every entry from that username
 * is removed from the eligible pool. `eligibleEntries` is the only way the
 * draw engine gets its pool, and the engine re-checks `hasWon` before
 * committing a winner.
 */

/** Canonical comparison key for a username: NFC, no leading @, lower-case. */
export function userKey(username) {
  return String(username ?? '')
    .normalize('NFC')
    .trim()
    .replace(/^@+/, '')
    .toLowerCase();
}

/** Text normalisation used for keyword matching. */
export function matchText(text) {
  return String(text ?? '')
    .normalize('NFC')
    .toLocaleLowerCase();
}

/** Splits "a, b\nc" into ["a", "b", "c"]. */
export function parseList(text) {
  return String(text ?? '')
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Turns raw comments into entries.
 * @param {Array<{id:string, username:string, displayName:string, comment:string}>} comments
 * @param {{keywords?: string, uniqueOnly?: boolean, exclude?: string}} filters
 */
export function buildEntries(comments, { keywords = '', uniqueOnly = false, exclude = '' } = {}) {
  const needles = parseList(keywords).map(matchText);
  const excluded = new Set(parseList(exclude).map(userKey));
  const seen = new Set();
  const entries = [];

  for (const comment of comments ?? []) {
    const key = userKey(comment.username);
    if (!key || excluded.has(key)) continue;
    if (needles.length) {
      const haystack = matchText(comment.comment);
      if (!needles.some((needle) => haystack.includes(needle))) continue;
    }
    if (uniqueOnly) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    entries.push({ ...comment, key });
  }
  return entries;
}

/** Usernames that may never be drawn again (all winners, including voided ones). */
export function winnerKeys(winners) {
  return new Set((winners ?? []).map((w) => userKey(w.username)));
}

export function hasWon(username, winners) {
  return winnerKeys(winners).has(userKey(username));
}

/** The pool a draw may pick from: entries minus every user who has already won. */
export function eligibleEntries(entries, winners) {
  const excluded = winnerKeys(winners);
  return entries.filter((entry) => !excluded.has(entry.key ?? userKey(entry.username)));
}

export function countPeople(entries) {
  return new Set(entries.map((entry) => entry.key ?? userKey(entry.username))).size;
}
